import { useState } from 'react';
import { FormFieldInputType } from 'models/enum';
import { ChartMetaFormField } from 'models/ui';
import { ErrorCard, ErrorFormField } from 'components/common';
import { ELSButton, ELSFlex, ELSFlexItem, ELSIcon } from 'components/common/els';
import {
  COLLECTION_CONFIGS,
  CollectionKey,
  EDFieldConfig,
  EDSectionConfig,
  EDStepConfig,
  FIELD_CONFIG_MAP,
  FIELD_TO_NOT_APPLICABLE_CONTROLLER,
  FormField,
  REPEATABLE_CONFIGS,
  RepeatableKey,
  STEP_CONFIGS,
  ViewTitle
} from './constants';
import AddEntryModal from './AddEntryModal';
import { EDRow } from './EmergencyDepartment';
import EmergencyDepartmentSelect from './EmergencyDepartmentSelect';
import EmergencyDepartmentTable from './EmergencyDepartmentTable';
import EmergencyDepartmentTimeField from './EmergencyDepartmentTimeField';
import RepeatableRows from './RepeatableRows';
import './emergency.department.scss';

export interface EmergencyDepartmentViewProps {
  activeStep: number;
  collections: Record<CollectionKey, EDRow[]>;
  completedSteps?: boolean[];
  displayAuthoringData?: Function;
  enableDisplayRecordsButton?: boolean;
  formFieldMap: Map<string, ChartMetaFormField>;
  formSubmittedCount: number;
  maxSelectableStep: number;
  isAuthor: boolean;
  onAddRepeatableRow: (key: RepeatableKey) => void;
  onBackStep: VoidFunction;
  onContinueStep: VoidFunction;
  onDeleteRepeatableRow: (key: RepeatableKey, index: number) => void;
  onReplaceCollection: (key: CollectionKey, rows: EDRow[]) => void;
  onRepeatableChange: (key: RepeatableKey, index: number, fieldKey: string, value: string) => void;
  onResetStep: VoidFunction;
  onSelectStep: (stepIndex: number) => void;
  repeatables: Record<RepeatableKey, EDRow[]>;
}

const ICON_SIZE = '1x';
const FIELD_CLASS = 'emergency-department__field';

const isChecked = (formField: ChartMetaFormField | undefined): boolean => formField?.value === true || String(formField?.value ?? '').toLowerCase() === 'true';

// Step 3: a checked "Not Applicable" toggle disables every field below it in that section.
const isDisabledByNotApplicable = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField): boolean => {
  const controller = FIELD_TO_NOT_APPLICABLE_CONTROLLER.get(fieldName);
  return Boolean(controller) && isChecked(formFieldMap.get(controller as FormField));
};

const getRenderedField = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField, isAuthor: boolean): ChartMetaFormField | undefined => {
  const formField = formFieldMap.get(fieldName);
  if (!formField) return formField;
  // Students (read-only) and fields under a checked Not-Applicable toggle are disabled.
  const disabled = !isAuthor || isDisabledByNotApplicable(formFieldMap, fieldName);
  return { ...formField, disabled };
};

const getFieldWidth = (fieldName: FormField): string => {
  const fieldConfig = FIELD_CONFIG_MAP.get(fieldName);
  if (fieldConfig?.type === FormFieldInputType.TEXT_AREA || fieldConfig?.type === FormFieldInputType.CHECK_BOX) return '1o1';
  return '1o3';
};

const renderCheckbox = (formField: ChartMetaFormField, fieldConfig: EDFieldConfig, isAuthor: boolean) => {
  const checked = isChecked(formField);
  const id = `field-checkbox-${formField.name}`;
  return (
    <label className="emergency-department__checkbox-field" htmlFor={id}>
      <input id={id} type="checkbox" checked={checked} disabled={!isAuthor} onChange={() => formField.onChange?.(formField, { target: { value: checked ? '' : 'true' } })} />
      <span>{fieldConfig.label}</span>
    </label>
  );
};

const renderLabelledControl = (formField: ChartMetaFormField, control: JSX.Element) => (
  <div className={FIELD_CLASS}>
    <div className="chart-input__label">{formField.label}</div>
    {control}
  </div>
);

const renderField = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField, isAuthor: boolean, formSubmittedCount: number) => {
  const formField = getRenderedField(formFieldMap, fieldName, isAuthor);
  const fieldConfig = FIELD_CONFIG_MAP.get(fieldName);
  if (!formField || !fieldConfig) return null;

  if (fieldConfig.type === FormFieldInputType.CHECK_BOX) {
    return <div className={FIELD_CLASS}>{renderCheckbox(formField, fieldConfig, isAuthor)}</div>;
  }

  if (fieldConfig.type === FormFieldInputType.TIME) {
    return renderLabelledControl(formField, <EmergencyDepartmentTimeField formField={formField} />);
  }

  if (fieldConfig.type === FormFieldInputType.DROPDOWN) {
    return renderLabelledControl(formField, <EmergencyDepartmentSelect formField={formField} />);
  }

  return <ErrorFormField className={FIELD_CLASS} formField={formField} formSubmittedCount={formSubmittedCount} formFieldMap={formFieldMap} />;
};

const sectionHasHeading = (section: EDSectionConfig, sectionIndex: number, stepTitle: string): boolean =>
  Boolean(section.title) && !(sectionIndex === 0 && section.title === stepTitle);

const getSectionClassName = (section: EDSectionConfig, sectionIndex: number, stepTitle: string): string => {
  const divided = sectionIndex > 0;
  const flush = divided && !sectionHasHeading(section, sectionIndex, stepTitle) && section.rows.length > 0;
  return ['emergency-department__section', divided && 'emergency-department__section--divided', flush && 'emergency-department__section--flush'].filter(Boolean).join(' ');
};

const renderSection = (
  section: EDSectionConfig,
  stepTitle: string,
  sectionIndex: number,
  formFieldMap: Map<string, ChartMetaFormField>,
  isAuthor: boolean,
  formSubmittedCount: number
) => (
  <div className={getSectionClassName(section, sectionIndex, stepTitle)} key={section.title || `section-${sectionIndex}`}>
    {sectionHasHeading(section, sectionIndex, stepTitle) && <h4 className="emergency-department__section-title">{section.title}</h4>}
    {section.rows.map((row) => (
      <ELSFlex className="u-flex-gutter-3x" key={row.join('-')} left wrap>
        {row.map((fieldName) => (
          <ELSFlexItem key={fieldName} md="1o1" w={getFieldWidth(fieldName)}>
            {renderField(formFieldMap, fieldName, isAuthor, formSubmittedCount)}
          </ELSFlexItem>
        ))}
      </ELSFlex>
    ))}
  </div>
);

const renderStepActions = (props: EmergencyDepartmentViewProps, isFirstStep: boolean, isLastStep: boolean) => {
  const { isAuthor, enableDisplayRecordsButton, onBackStep, onContinueStep, onResetStep, displayAuthoringData } = props;
  return (
    <div className="emergency-department__actions">
      {!isFirstStep && <ELSButton onClick={onBackStep} text="Back" type="secondary" iconName="chevron-left" iconPosition="left" iconSize={ICON_SIZE} />}

      {!isLastStep && <ELSButton onClick={onContinueStep} text="Continue" type="primary" iconName="chevron-right" iconPosition="right" iconSize={ICON_SIZE} />}

      {!isLastStep && isAuthor && <ELSButton type="secondary" text="Reset" onClick={onResetStep} />}

      {isLastStep && isAuthor && (
        <>
          <ELSButton onClick={onContinueStep} text="Save & Complete" type="primary" iconName="chevron-right" iconPosition="right" iconSize={ICON_SIZE} />
          <ELSButton type="secondary" text="Return with data" isDisabled={!enableDisplayRecordsButton} onClick={displayAuthoringData} />
        </>
      )}
    </div>
  );
};

const renderStepPanel = (
  props: EmergencyDepartmentViewProps,
  stepConfig: EDStepConfig,
  isFirstStep: boolean,
  isLastStep: boolean,
  onEditCollection: (key: CollectionKey) => void
) => {
  const { formFieldMap, formSubmittedCount, isAuthor, collections, repeatables, onRepeatableChange, onAddRepeatableRow, onDeleteRepeatableRow } = props;
  return (
    <div className="emergency-department__panel">
      {(stepConfig.sections || []).map((section, sectionIndex) => renderSection(section, stepConfig.title, sectionIndex, formFieldMap, isAuthor, formSubmittedCount))}

      {(stepConfig.collections || []).map((key) => (
        <EmergencyDepartmentTable key={key} config={COLLECTION_CONFIGS[key]} rows={collections[key] || []} isAuthor={isAuthor} onEdit={() => onEditCollection(key)} />
      ))}

      {(stepConfig.repeatables || []).map((key) => (
        <RepeatableRows
          key={key}
          config={REPEATABLE_CONFIGS[key]}
          rows={repeatables[key] || []}
          isAuthor={isAuthor}
          onChange={(index, fieldKey, value) => onRepeatableChange(key, index, fieldKey, value)}
          onAddRow={() => onAddRepeatableRow(key)}
          onDeleteRow={(index) => onDeleteRepeatableRow(key, index)}
        />
      ))}

      {renderStepActions(props, isFirstStep, isLastStep)}
    </div>
  );
};

const stepIndexClass = (isActive: boolean, showCompleted: boolean): string =>
  ['emergency-department__step-index', isActive && 'emergency-department__step-index--active', showCompleted && 'emergency-department__step-index--completed']
    .filter(Boolean)
    .join(' ');

const EmergencyDepartmentView = (props: EmergencyDepartmentViewProps) => {
  const { activeStep, collections, completedSteps = [], formFieldMap, formSubmittedCount, maxSelectableStep, isAuthor, onReplaceCollection, onSelectStep } = props;
  const [modalCollectionKey, setModalCollectionKey] = useState<CollectionKey | null>(null);
  const lastStepIndex = STEP_CONFIGS.length - 1;

  return (
    <div className="emergency-department">
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />
      <h1>{ViewTitle.EMERGENCY_DEPARTMENT}</h1>

      <div className="emergency-department__flow">
        {STEP_CONFIGS.map((stepConfig, stepIndex) => {
          const isActive = stepIndex === activeStep;
          const canAccessStep = !isAuthor || stepIndex <= maxSelectableStep;
          const showCompleted = Boolean(completedSteps[stepIndex]) && !isActive;

          return (
            <section className="emergency-department__step" key={stepConfig.title}>
              <button
                className={`emergency-department__step-header ${canAccessStep ? 'emergency-department__step-header--clickable' : 'emergency-department__step-header--locked'}`}
                disabled={!canAccessStep}
                onClick={() => onSelectStep(stepIndex)}
                type="button"
              >
                <span className={stepIndexClass(isActive, showCompleted)}>
                  {showCompleted ? <ELSIcon name="checkmark" size={ICON_SIZE} color="white" title="Completed" /> : stepIndex + 1}
                </span>
                <span className={`emergency-department__step-title${isActive ? ' emergency-department__step-title--active' : ''}`}>{stepConfig.title}</span>
              </button>

              {isActive && renderStepPanel(props, stepConfig, stepIndex === 0, stepIndex === lastStepIndex, setModalCollectionKey)}
            </section>
          );
        })}
      </div>

      {modalCollectionKey && (
        <AddEntryModal
          config={COLLECTION_CONFIGS[modalCollectionKey]}
          initialRows={collections[modalCollectionKey] || []}
          onCommit={(rows) => onReplaceCollection(modalCollectionKey, rows)}
          onClose={() => setModalCollectionKey(null)}
        />
      )}
    </div>
  );
};

EmergencyDepartmentView.displayName = 'EmergencyDepartmentView';
export default EmergencyDepartmentView;
